import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { graphqlApi } from '../../services/graphql';

/**
 * OAuth callback page for social media connect (opened in popup).
 * Receives the auth code, exchanges it via the existing backend GraphQL mutations,
 * then posts the result back to the opener window.
 */
const SocialConnectCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing...');
  // Guard against React StrictMode double-invoking the effect in dev, which
  // would exchange the same one-time auth code twice → second call fails with
  // Google "invalid_grant".
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');

    if (error) {
      postResult(detectPlatform(state), null, error);
      setStatus('error');
      setMessage(`Authorization failed: ${error}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No authorization code received.');
      return;
    }

    const platform = detectPlatform(state);
    exchangeCode(platform, code)
      .then((result) => {
        setStatus('success');
        setMessage(`Connected ${platform} successfully!`);
        postResult(platform, result, null);
        setTimeout(() => window.close(), 1500);
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err.message || 'Failed to connect');
        postResult(platform, null, err.message);
      });
  }, [searchParams]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center p-8">
        {status === 'processing' && (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
        )}
        {status === 'success' && <div className="text-green-600 text-4xl mb-4">✓</div>}
        {status === 'error' && <div className="text-red-600 text-4xl mb-4">✗</div>}
        <p className="text-gray-700">{message}</p>
      </div>
    </div>
  );
};

function postResult(platform: string, result: any, error: string | null) {
  window.opener?.postMessage({
    type: 'social-connect-result',
    platform,
    result,
    error,
  }, window.location.origin);
}

function detectPlatform(state: string | null): string {
  // The popup is a separate browsing context, so the opener's sessionStorage
  // is not reliably visible here. Primary detection: the platform is encoded
  // as a prefix in the state value (see socialConnect.ts). Fall back to
  // sessionStorage matching for older/in-flight sessions.
  if (state) {
    if (state.startsWith('youtube_') || state.startsWith('google_')) return 'youtube';
    if (state.startsWith('naverblog_') || state.startsWith('naver_')) return 'naverblog';
    if (state.startsWith('instagram_')) return 'instagram';
  }
  if (sessionStorage.getItem('naver_oauth_state') === state) return 'naverblog';
  if (sessionStorage.getItem('instagram_oauth_state') === state) return 'instagram';
  if (sessionStorage.getItem('google_oauth_state') === state) return 'youtube';
  return 'unknown';
}

async function exchangeCode(
  platform: string,
  code: string
): Promise<{
  platform: string;
  username: string;
  email?: string;
  channelId?: string;
  title?: string;
  handle?: string;
  subscriberCount?: number | null;
  hiddenSubscriberCount?: boolean | null;
  accountId?: string;
}> {
  const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY || 'da2-yirsmocw3fcdfkm35omcpgtley';
  const redirectUri = `${window.location.origin}/social-connect/callback`;

  if (!graphqlEndpoint) throw new Error('GraphQL endpoint not configured');

  if (platform === 'naverblog') {
    // Reuse the existing naverOAuth mutation
    const res = await fetch(graphqlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
      body: JSON.stringify({
        query: `mutation NaverOAuth($input: NaverOAuthInput!) {
          naverOAuth(input: $input) { success message error }
        }`,
        variables: { input: { code, redirectUri } },
      }),
    });
    const data = await res.json();
    const result = data?.data?.naverOAuth;
    if (!result?.success) throw new Error(result?.error || 'Naver connect failed');
    const userInfo = JSON.parse(result.message);
    return { platform: 'naverblog', username: userInfo.name || userInfo.email, email: userInfo.email };
  }

  if (platform === 'instagram') {
    // Instagram Login — the backend exchanges the code (server-side app
    // secret) and reads the user's own professional account via the Graph API.
    const res = await graphqlApi.connectInstagramAccount(code, redirectUri);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'Instagram connect failed');
    }
    const d = res.data;
    return {
      platform: 'instagram',
      username: d.username || d.accountId,
      accountId: d.accountId,
      subscriberCount: d.followersCount,
    };
  }

  if (platform === 'youtube') {
    // Google/YouTube OAuth — the backend exchanges the code (server-side
    // client_secret) and reads the user's own channel via channels.list.
    const res = await graphqlApi.connectYoutubeChannel(code, redirectUri);
    if (!res.success || !res.data) {
      throw new Error(res.error || 'YouTube connect failed');
    }
    const d = res.data;
    return {
      platform: 'youtube',
      username: d.handle || d.title || d.channelId,
      channelId: d.channelId,
      title: d.title,
      handle: d.handle,
      subscriberCount: d.subscriberCount,
      hiddenSubscriberCount: d.hiddenSubscriberCount,
    };
  }

  throw new Error(`Unsupported platform: ${platform}`);
}

export default SocialConnectCallback;
